import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { postPartyMovement, removePartyMovementBySource } from "@/lib/party-ledger";
import { fiscalYearLabel } from "@/features/purchase/constants";
import {
  computeLineAmounts,
  computePoTotals,
  type ComputedLine,
} from "@/features/purchase/api";
import type { CreditNoteFormValues } from "@/features/credit-notes/schema";
import type {
  CreditNote,
  CreditNoteLine,
  CreditNoteLineTax,
  CreditNoteListRow,
  CreditNoteStatus,
} from "@/features/credit-notes/types";
import type { TaxRate } from "@/features/settings/types";

const text = (value: string | undefined) => value?.trim() || null;

function money(value: unknown) {
  const n = typeof value === "number" ? value : Number(value ?? 0);
  return Number.isNaN(n) ? 0 : n;
}

function hydrateCn(raw: CreditNote): CreditNote {
  return {
    ...raw,
    subtotal: money(raw.subtotal),
    discount: money(raw.discount),
    tax_total: money(raw.tax_total),
    adjustment: money(raw.adjustment),
    grand_total: money(raw.grand_total),
  };
}

function hydrateLine(raw: CreditNoteLine): CreditNoteLine {
  return {
    ...raw,
    qty: money(raw.qty),
    weight: money(raw.weight),
    unit_rate: money(raw.unit_rate),
    line_subtotal: money(raw.line_subtotal),
    gst_slab: money(raw.gst_slab),
    taxes: (raw.taxes ?? []).map((t) => ({
      ...t,
      rate: money(t.rate),
      amount: money(t.amount),
    })),
  };
}

async function nextCnNumber(issueDate: string): Promise<string> {
  const { data: company } = await supabase
    .from("company")
    .select("credit_note_prefix, fiscal_year_start_month")
    .limit(1)
    .maybeSingle();
  const prefix =
    ((company?.credit_note_prefix as string | undefined) ?? "CN").trim().toUpperCase() || "CN";
  const fy = fiscalYearLabel(issueDate, Number(company?.fiscal_year_start_month) || 4);
  const pattern = `${prefix}-${fy}-`;
  const { data, error } = await supabase
    .from("credit_notes")
    .select("cn_number")
    .ilike("cn_number", `${pattern}%`)
    .order("cn_number", { ascending: false })
    .limit(1);
  if (error) throw error;
  const last = data?.[0]?.cn_number as string | undefined;
  const n = last ? Number.parseInt(last.replace(pattern, ""), 10) : 0;
  return `${pattern}${String((Number.isNaN(n) ? 0 : n) + 1).padStart(4, "0")}`;
}

async function loadClientPos(clientId: string) {
  const { data, error } = await supabase
    .from("clients")
    .select("place_of_supply, state, name, code, addr1, city, pincode")
    .eq("id", clientId)
    .single();
  if (error) throw error;
  return data as {
    place_of_supply: string | null;
    state: string;
    name: string;
    code: string;
    addr1: string;
    city: string;
    pincode: string;
  };
}

async function loadCompanyState() {
  const { data } = await supabase.from("company").select("state").limit(1).maybeSingle();
  return data as { state: string | null } | null;
}

async function loadTaxRates(): Promise<TaxRate[]> {
  const { data, error } = await supabase.from("tax_rates").select("*").eq("is_active", true);
  if (error) throw error;
  return (data ?? []).map((r) => ({
    ...(r as TaxRate),
    rate: money((r as TaxRate).rate),
    gst_slab:
      (r as TaxRate).gst_slab === null || (r as TaxRate).gst_slab === undefined
        ? null
        : money((r as TaxRate).gst_slab),
  }));
}

async function replaceLines(
  cnId: string,
  values: CreditNoteFormValues,
  rates: TaxRate[],
  companyState: string | null | undefined,
  clientPos: string | null | undefined,
) {
  const { error: delErr } = await supabase
    .from("credit_note_lines")
    .delete()
    .eq("credit_note_id", cnId);
  if (delErr) throw delErr;

  const computedLines: ComputedLine[] = [];
  for (let i = 0; i < values.lines.length; i++) {
    const line = values.lines[i];
    const computed = computeLineAmounts(
      line,
      rates,
      companyState,
      clientPos,
      values.issue_date,
    );
    computedLines.push(computed);

    const { data: inserted, error: lineErr } = await supabase
      .from("credit_note_lines")
      .insert({
        credit_note_id: cnId,
        line_no: i + 1,
        material_id: line.material_id,
        material_grade_id: text(line.material_grade_id),
        shape_size_id: text(line.shape_size_id),
        description: text(line.description),
        qty: line.qty,
        weight: line.weight,
        uom_id: text(line.uom_id),
        unit_rate: line.unit_rate,
        line_subtotal: computed.line_subtotal,
        gst_slab: line.gst_slab,
      })
      .select("id")
      .single();
    if (lineErr) throw lineErr;

    if (computed.taxes.length) {
      const { error: taxErr } = await supabase.from("credit_note_line_taxes").insert(
        computed.taxes.map((t) => ({
          line_id: inserted.id,
          tax_rate_id: t.tax_rate_id,
          tax_type: t.tax_type,
          rate: t.rate,
          amount: t.amount,
        })),
      );
      if (taxErr) throw taxErr;
    }
  }

  return computePoTotals(computedLines, values.discount, values.adjustment);
}

async function syncArCreditNote(cn: CreditNote) {
  if (cn.status === "issued" && cn.grand_total > 0) {
    await postPartyMovement({
      party: "client",
      partyId: cn.client_id,
      kind: "credit_note",
      amount: -Math.abs(cn.grand_total),
      movementDate: cn.issue_date,
      reference: cn.cn_number,
      notes: cn.invoice_number
        ? `Against ${cn.invoice_number}${cn.reason ? ` · ${cn.reason}` : ""}`
        : cn.reason,
      sourceType: "credit_note",
      sourceId: cn.id,
    });
  } else if (cn.status === "cancelled" || cn.status === "draft") {
    await removePartyMovementBySource({
      party: "client",
      partyId: cn.client_id,
      sourceType: "credit_note",
      sourceId: cn.id,
    });
  }
}

export function creditNoteToForm(cn: CreditNote): CreditNoteFormValues {
  return {
    client_id: cn.client_id,
    invoice_id: cn.invoice_id ?? "",
    issue_date: cn.issue_date,
    reason: cn.reason ?? "",
    bill_to_text: cn.bill_to_text ?? "",
    discount: cn.discount,
    adjustment: cn.adjustment,
    notes: cn.notes ?? "",
    lines: (cn.lines ?? []).map((line) => ({
      id: line.id,
      material_id: line.material_id,
      material_grade_id: line.material_grade_id ?? "",
      shape_size_id: line.shape_size_id ?? "",
      description: line.description ?? "",
      qty: line.qty,
      weight: line.weight,
      uom_id: line.uom_id ?? "",
      unit_rate: line.unit_rate,
      gst_slab: line.gst_slab,
    })),
  };
}

export function useCreditNotes(filters?: { clientId?: string }) {
  return useQuery({
    queryKey: ["credit_notes", filters?.clientId ?? "all"],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<CreditNoteListRow[]> => {
      let q = supabase
        .from("credit_notes")
        .select("*, clients(code, name), invoices(invoice_number), credit_note_lines(id)")
        .order("issue_date", { ascending: false })
        .order("created_at", { ascending: false });
      if (filters?.clientId) q = q.eq("client_id", filters.clientId);
      const { data, error } = await q;
      if (error) throw error;

      return (data ?? []).map((row) => {
        const raw = row as Record<string, unknown>;
        const client = raw.clients as { code?: string; name?: string } | null;
        const inv = raw.invoices as { invoice_number?: string } | null;
        const lines = raw.credit_note_lines as { id: string }[] | null;
        const { clients: _c, invoices: _i, credit_note_lines: _l, ...rest } = raw;
        return {
          ...hydrateCn(rest as unknown as CreditNote),
          client_code: client?.code ?? null,
          client_name: client?.name ?? null,
          invoice_number: inv?.invoice_number ?? null,
          line_count: lines?.length ?? 0,
        };
      });
    },
  });
}

export function useCreditNote(id?: string) {
  return useQuery({
    queryKey: ["credit_notes", id],
    enabled: isSupabaseConfigured && Boolean(id),
    queryFn: async (): Promise<CreditNote> => {
      const { data, error } = await supabase
        .from("credit_notes")
        .select(
          `*,
          clients(code, name, place_of_supply, state),
          invoices(invoice_number),
          credit_note_lines(
            *,
            credit_note_line_taxes(*),
            materials(name),
            material_grades(name),
            shape_sizes(size_label)
          )`,
        )
        .eq("id", id!)
        .single();
      if (error) throw error;

      const raw = data as Record<string, unknown>;
      const client = raw.clients as { code?: string; name?: string } | null;
      const inv = raw.invoices as { invoice_number?: string } | null;
      const linesRaw = (raw.credit_note_lines as Record<string, unknown>[] | null) ?? [];
      const { clients: _c, invoices: _i, credit_note_lines: _l, ...rest } = raw;

      const lines = linesRaw
        .map((line) => {
          const taxes = ((line.credit_note_line_taxes as CreditNoteLineTax[] | null) ?? []).map(
            (t) => ({ ...t, rate: money(t.rate), amount: money(t.amount) }),
          );
          const material = line.materials as { name?: string } | null;
          const grade = line.material_grades as { name?: string } | null;
          const shape = line.shape_sizes as { size_label?: string } | null;
          const {
            credit_note_line_taxes: _t,
            materials: _m,
            material_grades: _g,
            shape_sizes: _s,
            ...lineRest
          } = line;
          return hydrateLine({
            ...(lineRest as unknown as CreditNoteLine),
            taxes,
            material_name: material?.name ?? null,
            grade_name: grade?.name ?? null,
            shape_label: shape?.size_label ?? null,
          });
        })
        .sort((a, b) => a.line_no - b.line_no);

      return {
        ...hydrateCn(rest as unknown as CreditNote),
        client_code: client?.code ?? null,
        client_name: client?.name ?? null,
        invoice_number: inv?.invoice_number ?? null,
        lines,
      };
    },
  });
}

async function insertCreditNote(
  values: CreditNoteFormValues,
  opts: { status: CreditNoteStatus; userId: string | undefined },
) {
  const [client, company, rates] = await Promise.all([
    loadClientPos(values.client_id),
    loadCompanyState(),
    loadTaxRates(),
  ]);

  if (text(values.invoice_id)) {
    const { data: inv, error: invErr } = await supabase
      .from("invoices")
      .select("id, client_id, status, invoice_number")
      .eq("id", values.invoice_id)
      .single();
    if (invErr) throw invErr;
    if (String(inv.client_id) !== values.client_id) {
      throw new Error("Invoice belongs to a different client");
    }
  }

  const cnNumber = await nextCnNumber(values.issue_date);
  const billTo =
    text(values.bill_to_text) ??
    [client.name, client.addr1, client.city, client.pincode].filter(Boolean).join("\n");

  const { data: header, error } = await supabase
    .from("credit_notes")
    .insert({
      cn_number: cnNumber,
      client_id: values.client_id,
      invoice_id: text(values.invoice_id),
      issue_date: values.issue_date,
      status: opts.status,
      reason: text(values.reason),
      bill_to_text: billTo,
      notes: text(values.notes),
      discount: values.discount,
      adjustment: values.adjustment,
      created_by: opts.userId ?? null,
      updated_by: opts.userId ?? null,
    })
    .select("*")
    .single();
  if (error) throw error;

  const clientPos = client.place_of_supply || client.state;
  const totals = await replaceLines(header.id, values, rates, company?.state, clientPos);

  const { data: updated, error: updErr } = await supabase
    .from("credit_notes")
    .update({ ...totals, updated_at: new Date().toISOString() })
    .eq("id", header.id)
    .select("*")
    .single();
  if (updErr) throw updErr;

  const cn = hydrateCn(updated as CreditNote);
  if (opts.status === "issued") {
    let invoiceNumber: string | null = null;
    if (cn.invoice_id) {
      const { data: inv } = await supabase
        .from("invoices")
        .select("invoice_number")
        .eq("id", cn.invoice_id)
        .maybeSingle();
      invoiceNumber = (inv?.invoice_number as string | undefined) ?? null;
    }
    await syncArCreditNote({ ...cn, invoice_number: invoiceNumber });
  }
  return cn;
}

export function useCreateCreditNote() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      values,
      issue = false,
    }: {
      values: CreditNoteFormValues;
      issue?: boolean;
    }) => insertCreditNote(values, { status: issue ? "issued" : "draft", userId: user?.id }),
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["credit_notes"] });
      await queryClient.invalidateQueries({ queryKey: ["client_ar_movements"] });
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      await queryClient.invalidateQueries({ queryKey: ["master_ledger"] });
      toast.success(vars.issue ? "Credit note issued" : "Credit note draft saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to create credit note"),
  });
}

export function useUpdateCreditNote() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      values,
      replaceLines: doReplace,
      issue = false,
    }: {
      id: string;
      values: CreditNoteFormValues;
      replaceLines: boolean;
      issue?: boolean;
    }) => {
      const { data: existing, error: exErr } = await supabase
        .from("credit_notes")
        .select("*")
        .eq("id", id)
        .single();
      if (exErr) throw exErr;
      const cn = hydrateCn(existing as CreditNote);
      if (cn.status !== "draft") throw new Error("Only draft credit notes can be edited");

      const [client, company, rates] = await Promise.all([
        loadClientPos(values.client_id),
        loadCompanyState(),
        loadTaxRates(),
      ]);

      const patch: Record<string, unknown> = {
        client_id: values.client_id,
        invoice_id: text(values.invoice_id),
        issue_date: values.issue_date,
        reason: text(values.reason),
        bill_to_text: text(values.bill_to_text),
        notes: text(values.notes),
        discount: values.discount,
        adjustment: values.adjustment,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };
      if (issue) patch.status = "issued";

      if (doReplace) {
        const clientPos = client.place_of_supply || client.state;
        const totals = await replaceLines(id, values, rates, company?.state, clientPos);
        Object.assign(patch, totals);
      }

      const { data: updated, error } = await supabase
        .from("credit_notes")
        .update(patch)
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;

      const result = hydrateCn(updated as CreditNote);
      if (issue) {
        let invoiceNumber: string | null = null;
        if (result.invoice_id) {
          const { data: inv } = await supabase
            .from("invoices")
            .select("invoice_number")
            .eq("id", result.invoice_id)
            .maybeSingle();
          invoiceNumber = (inv?.invoice_number as string | undefined) ?? null;
        }
        await syncArCreditNote({ ...result, invoice_number: invoiceNumber });
      }
      return result;
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["credit_notes"] });
      await queryClient.invalidateQueries({ queryKey: ["credit_notes", vars.id] });
      await queryClient.invalidateQueries({ queryKey: ["client_ar_movements"] });
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      await queryClient.invalidateQueries({ queryKey: ["master_ledger"] });
      toast.success(vars.issue ? "Credit note issued" : "Credit note saved");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to save credit note"),
  });
}

export function useUpdateCreditNoteStatus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: CreditNoteStatus }) => {
      const { data: existing, error: exErr } = await supabase
        .from("credit_notes")
        .select("*")
        .eq("id", id)
        .single();
      if (exErr) throw exErr;
      const cn = hydrateCn(existing as CreditNote);

      if (status === "issued" && cn.status !== "draft") {
        throw new Error("Only drafts can be issued");
      }

      const { data, error } = await supabase
        .from("credit_notes")
        .update({
          status,
          updated_by: user?.id ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;

      const updated = hydrateCn(data as CreditNote);
      let invoiceNumber: string | null = null;
      if (updated.invoice_id) {
        const { data: inv } = await supabase
          .from("invoices")
          .select("invoice_number")
          .eq("id", updated.invoice_id)
          .maybeSingle();
        invoiceNumber = (inv?.invoice_number as string | undefined) ?? null;
      }
      await syncArCreditNote({ ...updated, invoice_number: invoiceNumber });
      return updated;
    },
    onSuccess: async (_d, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["credit_notes"] });
      await queryClient.invalidateQueries({ queryKey: ["credit_notes", vars.id] });
      await queryClient.invalidateQueries({ queryKey: ["client_ar_movements"] });
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      await queryClient.invalidateQueries({ queryKey: ["master_ledger"] });
      toast.success("Status updated");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to update status"),
  });
}

export function useDeleteCreditNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error: fetchErr } = await supabase
        .from("credit_notes")
        .select("status")
        .eq("id", id)
        .single();
      if (fetchErr) throw fetchErr;
      if ((data as { status: CreditNoteStatus }).status !== "draft") {
        throw new Error("Only draft credit notes can be deleted");
      }
      const { error } = await supabase.from("credit_notes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["credit_notes"] });
      toast.success("Credit note deleted");
    },
    onError: (error: Error) => toast.error(error.message || "Failed to delete credit note"),
  });
}
